import { customElement, property, query } from 'lit/decorators.js';
import {html, LitElement} from "lit";
import {modalStyle} from "@/components/modal/modal.styles";

/**
 * An example element.
 *
 * @slot - This element has a slot
 */
@customElement('sbk-modal')
export class SbkModal extends LitElement {

    @property()
    heading = '';

    @property({type: Boolean})
    open = false;

    @property({type: Boolean})
    slotContent = false;

    @property({type: Boolean})
        // eslint-disable-next-line lit/attribute-names
    hideCloseButton = false;

    @property()
    icon: 'warning' | 'success' | null = null;

    @query('slot[name="footer"]') _footerSlot!: HTMLSlotElement;

    async _showModal(e: Event) {
        e.preventDefault();
        this.open = true;
        const modal = this.renderRoot.querySelector('dialog');
        if (modal) {
            modal.showModal();
        }
        document.body.style.overflow = 'hidden'
        await this.updateComplete
        const modalOpenEvent = new CustomEvent('modal-open');
        this.dispatchEvent(modalOpenEvent);
    }

    _closeModal(e: Event) {
        this.open = false;
        const modalBeforeCloseEvent = new CustomEvent('modal-before-close',
            {
                bubbles: true,
                cancelable: true,
                detail: {id: this.id},
            });
        this.dispatchEvent(modalBeforeCloseEvent);
        if (modalBeforeCloseEvent.defaultPrevented) {
            e.preventDefault();
        }
        const modal = this.renderRoot.querySelector('dialog');
        if (modal && !modalBeforeCloseEvent.defaultPrevented) {
            modal.close();
        }
        document.body.style.overflow = ''
        const modalClosedEvent = new CustomEvent('modal-closed', {
            detail: {id: this.id},
        });
        this.dispatchEvent(modalClosedEvent);
    }

    static get styles() {
        return modalStyle;
    }

    firstUpdated() {
        this.slotContent = this._hasSlotContent();
    }

    _renderIcon() {
        switch (this.icon) {
            case 'warning':
                return html`
                    <sbk-icon size="112" symbol='modal-warning'></sbk-icon>
                `;
            case 'success':
                return html`
                    <sbk-icon size="112" symbol='modal-success'></sbk-icon>
                `;
            default:
                return null;
        }
    }

    render() {
        return html`
            <slot name="triggerElement" @click=${this._showModal}></slot>
            <dialog part="dialog" class="modal inner-border">
                <header class="modal__header">
                    ${this.heading ? html`<h3 tabindex="0">${this.heading}</h3>` : ''}
                    ${!this.hideCloseButton ? html`
                        <sbk-round-button size="medium" variant="round" icon="close" class="close-button"
                                    @click=${this._closeModal}></sbk-round-button>` : ''}
                </header>
                <div class="modal__content">
                    ${this.icon ? html`
                        <div class="modal__content--icon-wrapper">${this._renderIcon()}</div>` : ''}
                    <slot></slot>
                    <slot name="settings" class="modal__content--settings"></slot>
                    <slot name="link"></slot>
                </div>
                <footer class="modal__footer ${!this.slotContent ? 'modal__footer--hidden' : ''}">
                    <slot name="footer"></slot>
                </footer>
            </dialog>

        `;
    }

    _hasSlotContent() {
        const footerSlot = this.shadowRoot?.querySelector('slot[name="footer"]') as HTMLSlotElement;
        // eslint-disable-next-line
        return footerSlot?.assignedNodes({flatten: true}).length > 0 || false;
    }
}
